/**
 * Free-text muscle name → muscle id.
 *
 * The backend has no muscle table and no enum: `external_exercises.target` and
 * `custom_exercises.target` are plain strings, and `StoreCustomExerciseRequest`
 * validates `target` only as `nullable|string|max:100`. Users have already
 * typed dozens of distinct values. So matching is lowercase-substring,
 * first-match-wins — an enum would reject data the API happily stores.
 *
 * ORDER IS LOAD-BEARING. Every entry above the single-word block is there
 * because a looser pattern below it would otherwise win and land on the wrong
 * part of the body. Two of those were live bugs in the previous web-only table:
 *
 * - `'delt'` was missing entirely (only `'deltoid'` and `'shoulder'` existed),
 *   so the catalogue's own primary target `Delts` matched nothing and 143
 *   exercises drew a blank figure.
 * - `'ab'` sat above `'abductor'`, and `'abductors'.includes('ab')` is true, so
 *   `Abductors` resolved to the abdominals.
 *
 * Adding a pattern here means re-checking the ordering tests in
 * `__tests__/muscle-aliases.test.ts`, which pin every trap below.
 */

import { MUSCLE_IDS } from './muscle-ids';

import type { MuscleId } from './muscle-map.types';

type MuscleAlias = readonly [pattern: string, muscles: readonly MuscleId[]];

const MUSCLE_ALIASES: readonly MuscleAlias[] = [
  // --- Compound and specific names, ahead of the looser patterns they contain.
  ['sternocleido', ['neck']],
  ['levator scapulae', ['neck']],
  // 'shoulder blades' is trapezius/rhomboid territory, not deltoid.
  ['shoulder blade', ['traps']],
  ['rotator cuff', ['shoulders']],
  // Both must beat the bare 'back' pattern at the bottom of the table.
  ['upper back', ['traps']],
  ['lower back', ['lower_back']],
  ['lower ab', ['abs']],
  ['inner thigh', ['adductors']],
  // 'hip flexor' before 'hip'; but 'hip abductor'/'hip adductor' have to reach
  // the abductor/adductor patterns instead, so they sit above this.
  ['hip abductor', ['glutes']],
  ['hip adductor', ['adductors']],
  ['hip flexor', ['hips']],
  ['iliotibial', ['glutes']],
  ['erector', ['lower_back']],
  ['thoracic', ['lower_back']],
  ['latissimus', ['lats']],
  // Ahead of anything matching 'anterior'.
  ['serratus', ['obliques']],
  // The map's job is to discriminate; cardio names it cannot place are left
  // dark and the chips carry the label instead.
  ['cardiovascular', []],
  ['full body', MUSCLE_IDS],

  // --- Single-word patterns.
  ['abductor', ['glutes']],
  ['adductor', ['adductors']],
  ['abdominal', ['abs']],
  ['diaphragm', ['abs']],
  ['oblique', ['obliques']],
  ['pectoral', ['chest']],
  ['chest', ['chest']],
  // 'deltoid' must precede 'lat', or 'lateral deltoids' lands on the lats.
  ['deltoid', ['shoulders']],
  ['delt', ['shoulders']],
  ['shoulder', ['shoulders']],
  ['rhomboid', ['traps']],
  ['trap', ['traps']],
  ['neck', ['neck']],
  // Brachialis is an elbow flexor sitting under the biceps, not a forearm.
  ['brachial', ['biceps']],
  ['bicep', ['biceps']],
  ['tricep', ['triceps']],
  // 'forearm' must precede 'arms', since 'forearms' contains it.
  ['forearm', ['forearms']],
  ['wrist', ['forearms']],
  ['hand', ['forearms']],
  ['grip', ['forearms']],
  ['core', ['abs']],
  ['abs', ['abs']],
  ['lat', ['lats']],
  ['spine', ['lower_back']],
  ['glute', ['glutes']],
  ['quad', ['quads']],
  ['hamstring', ['hamstrings']],
  ['soleus', ['calves']],
  ['calves', ['calves']],
  ['calf', ['calves']],
  ['shin', ['calves']],
  ['ankle', ['ankles']],
  ['feet', ['ankles']],
  ['foot', ['ankles']],
  ['hip', ['hips']],
  ['groin', ['adductors']],
  ['arms', ['biceps', 'triceps', 'forearms']],
  // Last resort: a bare 'back' is the lats once the qualified forms are gone.
  ['back', ['lats']],
];

/**
 * Resolve one free-text muscle name to zero or more muscle ids. Returns `[]`
 * for names the figure cannot place, which is a legitimate outcome — the chip
 * row still shows the name.
 */
export function musclesForName(name: string): MuscleId[] {
  const needle = name.trim().toLowerCase();
  if (!needle) return [];

  const hit = MUSCLE_ALIASES.find(([pattern]) => needle.includes(pattern));
  return hit ? [...hit[1]] : [];
}

/**
 * Resolve several names at once: deduplicated, and ordered head to toe rather
 * than by the order they arrived in.
 *
 * The order matters because these lists are read aloud in copy ("sore today:
 * chest, lats") and a stable one keeps the same set of muscles from reading
 * differently between two surfaces. It is the figure's own order, which is
 * also the backend's — `MuscleGroup::forNames` sorts by the same case list.
 */
export function musclesForNames(
  names: readonly string[] | null | undefined
): MuscleId[] {
  if (!names || names.length === 0) return [];

  const found = new Set(names.flatMap((name) => musclesForName(name)));
  return MUSCLE_IDS.filter((id) => found.has(id));
}
